import { useCallback, useRef } from 'preact/hooks';

interface UseDoubleTapOptions {
  /** Max ms between two taps to count as a double-tap (default 300) */
  delay?: number;
  /** Max px of movement allowed between taps (default 10) */
  movementThreshold?: number;
}

/**
 * Returns a `onPointerDown` handler that fires `callback` on double-tap.
 *
 * Usage:
 *   const onDoubleTap = useDoubleTap(() => openCompose());
 *   <div onPointerDown={onDoubleTap} />
 *
 * Movement guard: if the pointer moves more than `movementThreshold` px between
 * the first tap and the second tap, the sequence is discarded (treats it as a
 * scroll/drag gesture, not a double-tap).
 */
export function useDoubleTap(
  callback: (e: PointerEvent) => void,
  options: UseDoubleTapOptions = {},
): (e: PointerEvent) => void {
  const { delay = 300, movementThreshold = 10 } = options;
  const lastTapRef = useRef<{ time: number; x: number; y: number } | null>(null);

  return useCallback(
    (e: PointerEvent) => {
      const now = Date.now();
      const prev = lastTapRef.current;

      if (
        prev !== null &&
        now - prev.time <= delay &&
        Math.abs(e.clientX - prev.x) <= movementThreshold &&
        Math.abs(e.clientY - prev.y) <= movementThreshold
      ) {
        // Second tap within window — fire and reset
        lastTapRef.current = null;
        callback(e);
      } else {
        // First tap — record position and time
        lastTapRef.current = { time: now, x: e.clientX, y: e.clientY };
      }
    },
    [callback, delay, movementThreshold],
  );
}
